import { Fragment } from "react";
import TeamMemberNameBadge from "../teamRoles/TeamMemberNameBadge";
import { isValidHexColor } from "../teamRoles/teamRoleColorUtils";

export const COMPLETE_STEP_STATUS = 2;

export const isStepCompleted = (step) =>
    step?.status === COMPLETE_STEP_STATUS;

const getEmployeeId = (employee) =>
    employee?.employeeId ?? employee?.id ?? null;

export const getEmployeeDisplayName = (employee) => {
    if (!employee) return "";
    if (typeof employee.fullName === "string") {
        return employee.fullName.trim();
    }

    return [employee.firstName, employee.lastName]
        .filter(Boolean)
        .join(" ")
        .trim();
};

export const getEmployeeTeamRoleColorHex = (employee, teamMembers = []) => {
    if (isValidHexColor(employee?.teamRoleColorHex)) {
        return employee.teamRoleColorHex.trim();
    }

    const employeeId = getEmployeeId(employee);
    if (!employeeId) return null;

    const availableTeamMembers = Array.isArray(teamMembers) ? teamMembers : [];
    const teamMember = availableTeamMembers.find(
        (member) => String(getEmployeeId(member)) === String(employeeId),
    );

    return isValidHexColor(teamMember?.teamRoleColorHex)
        ? teamMember.teamRoleColorHex.trim()
        : null;
};

const EmployeeName = ({ employee, name, colorHex, shouldColor }) => {
    const displayName = name ?? getEmployeeDisplayName(employee);
    const hasTeamRoleColor = shouldColor && isValidHexColor(colorHex);

    if (!displayName) return null;

    if (hasTeamRoleColor) {
        return (
            <TeamMemberNameBadge
                name={displayName}
                colorHex={colorHex}
                className="max-w-[180px] align-middle"
                title={displayName}
            />
        );
    }

    return <span className="font-medium text-sky-500">{displayName}</span>;
};

const AssignedEmployeeNames = ({ employees, shouldColor, teamMembers }) =>
    employees.map((employee, index) => (
        <Fragment key={getEmployeeId(employee) ?? index}>
            {index > 0 && ", "}
            <EmployeeName
                employee={employee}
                colorHex={getEmployeeTeamRoleColorHex(employee, teamMembers)}
                shouldColor={shouldColor}
            />
        </Fragment>
    ));

export default function StepEmployeeDetails({
    step,
    teamMembers = [],
    showLastModified = true,
    formatUpdatedDate,
}) {
    const isCompleted = isStepCompleted(step);
    const assignedEmployees = Array.isArray(step?.assignedEmployees)
        ? step.assignedEmployees
        : [];
    const completedByColorHex = isCompleted
        ? getEmployeeTeamRoleColorHex(step?.completedBy, teamMembers)
        : null;

    return (
        <>
            <div className="text-sm font-medium text-left">
                Assigned to{" "}
                <AssignedEmployeeNames
                    employees={assignedEmployees}
                    shouldColor={!isCompleted}
                    teamMembers={teamMembers}
                />
            </div>
            <div className="text-sm font-medium text-left">
                Assigned by{" "}
                <EmployeeName
                    employee={step?.assignedByEmployee}
                    shouldColor={false}
                />
            </div>
            {showLastModified && (
                <>
                    <div className="text-sm font-medium text-left">
                        Last modified by{" "}
                        <EmployeeName
                            employee={step?.lastModifiedBy}
                            shouldColor={false}
                        />
                    </div>
                    <div className="text-sm font-medium text-left">
                        Last modified at{" "}
                        {step?.lastModifiedBy != null && formatUpdatedDate && (
                            <span className="font-medium text-sky-500">
                                {formatUpdatedDate(
                                    step.updated ??
                                        step.Updated ??
                                        step.lastModifiedAt ??
                                        step.LastModifiedAt,
                                )}
                            </span>
                        )}
                    </div>
                </>
            )}
            <div className="text-sm font-medium text-left">
                Completed by{" "}
                <EmployeeName
                    employee={step?.completedBy}
                    colorHex={completedByColorHex}
                    shouldColor={isCompleted}
                />
            </div>
        </>
    );
}
